import type { JSX } from 'react';
import { useMemo, useState } from 'react';
import { AcceptCompanyInviteRequestSchema } from '@crv/contracts/auth';
import { setTokens, type AuthTokens } from '@crv/shared';
import { Alert, Button, Stack, TextField, Typography } from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { acceptCompanyInvite } from '../api/accept-invite';
import { setWebRefreshManagerTokens } from '../api/auth-refresh-manager';
import { ApiClientError } from '../api/http-client';
import { useTenancyStore } from '../tenancy/tenancy-store';
import { AuthShell } from './AuthShell';

type FieldErrors = Partial<Record<'password' | 'confirmPassword' | 'token', string>>;

export function AcceptInvitePage(): JSX.Element {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const setSelectedCompanyId = useTenancyStore((state) => state.setSelectedCompanyId);

  const token = useMemo(() => searchParams.get('token') ?? '', [searchParams]);

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [formError, setFormError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: acceptCompanyInvite,
    onSuccess: (response): void => {
      const tokens: AuthTokens = {
        accessToken: response.accessToken,
        refreshToken: response.refreshToken,
      };
      setTokens(tokens);
      setWebRefreshManagerTokens(tokens);
      queryClient.clear();
      setSelectedCompanyId(response.companyId);
      navigate('/employees', { replace: true });
    },
    onError: (error): void => {
      const message =
        error instanceof ApiClientError
          ? error.body?.message ?? error.message
          : error instanceof Error
            ? error.message
            : 'Unable to accept invite. Please try again.';
      setFormError(message);
    },
  });

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    setFieldErrors({});
    setFormError(null);

    const errors: FieldErrors = {};
    const parsed = AcceptCompanyInviteRequestSchema.safeParse({ token, password });
    if (!parsed.success) {
      for (const issue of parsed.error.issues) {
        const field = issue.path[0];
        if ((field === 'token' || field === 'password') && !errors[field]) {
          errors[field] = issue.message;
        }
      }
    }

    if (!confirmPassword.trim()) {
      errors.confirmPassword = errors.confirmPassword ?? 'Confirm your password.';
    }

    if (!errors.password && password.trim().length > 0 && password.length < 8) {
      errors.password = 'Password must be at least 8 characters.';
    }

    if (!errors.confirmPassword && password && confirmPassword && password !== confirmPassword) {
      errors.confirmPassword = 'Passwords do not match.';
    }

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }

    mutation.mutate(parsed.success ? parsed.data : { token, password });
  };

  return (
    <AuthShell title="Accept invite" subtitle="Set a password to join your company workspace.">
      <form onSubmit={handleSubmit} noValidate>
        <Stack spacing={2.5}>
          <TextField
            label="Password"
            type="password"
            value={password}
            onChange={(event) => {
              setPassword(event.target.value);
            }}
            autoComplete="new-password"
            required
            fullWidth
            error={Boolean(fieldErrors.password)}
            helperText={fieldErrors.password ?? 'Minimum 8 characters.'}
          />

          <TextField
            label="Confirm password"
            type="password"
            value={confirmPassword}
            onChange={(event) => {
              setConfirmPassword(event.target.value);
            }}
            autoComplete="new-password"
            required
            fullWidth
            error={Boolean(fieldErrors.confirmPassword)}
            helperText={fieldErrors.confirmPassword ?? ' '}
          />

          {fieldErrors.token && (
            <Alert severity="error">
              <Typography variant="body2">{fieldErrors.token}</Typography>
            </Alert>
          )}

          {formError && <Alert severity="error">{formError}</Alert>}

          <Button type="submit" variant="contained" size="large" disabled={mutation.isPending}>
            {mutation.isPending ? 'Accepting invite...' : 'Accept invite'}
          </Button>
        </Stack>
      </form>
    </AuthShell>
  );
}
